import { ApiExplorerInput } from '@squareup/dex-types-shared-api-explorer';
import { useContext, useMemo } from 'react';

import {
  ApiExplorerContext,
  ApiExplorerContextInstance,
} from './ApiExplorerProvider';

const useApiExplorerContext = () => {
  return useContext(ApiExplorerContextInstance);
};

const useApiExplorerGraph = () => {
  const { graph } = useApiExplorerContext();

  return useMemo(() => {
    return graph;
  }, [graph]);
};

const useApiExplorerEndpoint = () => {
  const { endpoint } = useApiExplorerContext();

  return useMemo(() => {
    return endpoint;
  }, [endpoint]);
};

const useApiExplorerApi = () => {
  const { api } = useApiExplorerContext();

  return useMemo(() => {
    return api;
  }, [api]);
};

const useApiExplorerInput = (): [
  ApiExplorerInput,
  ApiExplorerContext['dispatchInputAction']
] => {
  const { input, dispatchInputAction } = useApiExplorerContext();

  return useMemo(() => {
    return [input, dispatchInputAction];
  }, [input, dispatchInputAction]);
};

const useApiExplorerRequestInitiated = () => {
  const { onApiRequestInitiated } = useApiExplorerContext();

  return useMemo(() => {
    return onApiRequestInitiated;
  }, [onApiRequestInitiated]);
};

const useApiExplorerApiResponse = () => {
  const { apiResponse, isApiRequestRunning } = useApiExplorerContext();

  return useMemo(() => {
    return { apiResponse, isApiRequestRunning };
  }, [apiResponse, isApiRequestRunning]);
};

const useApiExplorerAccessToken = () => {
  const { accessTokenData } = useApiExplorerContext();
  return useMemo(() => accessTokenData.token, [accessTokenData]);
};

const useApiExplorerEnvironment = () => {
  const { accessTokenData } = useApiExplorerContext();
  return useMemo(() => accessTokenData.environment, [accessTokenData]);
};

const useApiExplorerAccessTokenData = () => {
  const { accessTokenData } = useApiExplorerContext();
  return useMemo(() => accessTokenData, [accessTokenData]);
};

const useApiExplorerFieldDropdownOptionsMap = () => {
  const { apiExplorerFieldDropdownOptionsMap } = useApiExplorerContext();
  return useMemo(
    () => apiExplorerFieldDropdownOptionsMap,
    [apiExplorerFieldDropdownOptionsMap]
  );
};

const stripFieldRegex = /\[\d+]/g;
const stripArraySyntaxFromFieldName = (field: string): string => {
  return field.replace(stripFieldRegex, '');
};

const useApiExplorerDropdownOptionsForField = (field: string) => {
  const accessToken = useApiExplorerAccessToken();
  const environment = useApiExplorerEnvironment();

  const apiExplorerFieldDropdownOptionsMap =
    useApiExplorerFieldDropdownOptionsMap();

  return useMemo(() => {
    const fieldName = stripArraySyntaxFromFieldName(field);
    const hash = `${fieldName}-${accessToken}-${environment}`;
    return (
      apiExplorerFieldDropdownOptionsMap[fieldName] ||
      apiExplorerFieldDropdownOptionsMap[hash] ||
      null
    );
  }, [accessToken, apiExplorerFieldDropdownOptionsMap, environment, field]);
};

const useApiExplorerCustomAttributeSchema = () => {
  const { customAttributeSchema } = useApiExplorerContext();
  return useMemo(() => customAttributeSchema, [customAttributeSchema]);
};

export {
  useApiExplorerContext,
  useApiExplorerGraph,
  useApiExplorerEndpoint,
  useApiExplorerApi,
  useApiExplorerInput,
  useApiExplorerRequestInitiated,
  useApiExplorerApiResponse,
  useApiExplorerAccessToken,
  useApiExplorerAccessTokenData,
  useApiExplorerEnvironment,
  useApiExplorerFieldDropdownOptionsMap,
  useApiExplorerDropdownOptionsForField,
  useApiExplorerCustomAttributeSchema,
};
